import { getCartesianBom } from "./api/api";

export function right(text, numChars = 1) {
  if (typeof text !== "string" || numChars === undefined || numChars < 0) {
    throw new TypeError("Invalid arguments");
  }
  return text.slice(Math.max(0, text.length - numChars));
}

export function left(text, numChars = 1) {
  if (typeof text !== "string" || numChars === undefined || numChars < 0) {
    throw new TypeError("Invalid arguments");
  }

  return text.slice(0, Math.min(numChars, text.length));
}

export function degreesToRadians(degrees) {
  return degrees * (Math.PI / 180);
}

export async function gatherCartesianProductsForBOM(configId) {
  const products = await getCartesianBom(configId);
  console.info("products", products);
  return products.flatMap((product) => {
    if (product?.length > 0 && product?.quantity > 0) {
      return Array.from({ length: product.quantity }, () => ({
        ...product,
        quantity: product.length
      }));
    }
    return [product];
  });
}

export function formatNumber(value, decimalPlaces) {
  // console.log("formatNumber", value, decimalPlaces);
  const numberValue = Number(value);
  if (isNaN(numberValue)) {
    console.error("Invalid chart input " + value);
    return 0;
  }
  return numberValue.toFixed(decimalPlaces);
}

export const generateMovesData = (movesArray, startPosition, directionCopy) => {
  const generatedMoves = [];
  let accumulatedTime = 0;
  let lastDistance = Number(startPosition);

  movesArray.forEach((move) => {
    // check if all necessary parameters are defined
    // if not, then return an empty array
    const { stepName, movementType, movement, tacc, tdec, travelTime, peak, aacc, adcc, direction, dwellTime, id } =
      move;

    if (!stepName || !movementType || typeof movement === "undefined" || !direction || !travelTime) {
      console.error("Missing parameters for move: ", move);
      return;
    }

    const data = [];
    let currentSpeed = 0;
    let currentDistance = 0;

    const keyPoints = [0, tacc, travelTime - tdec, travelTime].map(Number);

    keyPoints.forEach((time, index) => {
      if (time <= travelTime) {
        if (time < tacc) {
          currentSpeed = (aacc * time) / 1000;
        } else if (time < travelTime - tdec) {
          currentSpeed = peak;
        } else {
          const timeDecelerating = time - (travelTime - tdec);
          currentSpeed = peak - (adcc * timeDecelerating) / 1000;
        }

        if (index > 0) {
          const deltaTime = keyPoints[index] - keyPoints[index - 1];
          const avgSpeed = (Math.abs(currentSpeed) + Math.abs(data[data.length - 1]?.speed)) / 2;
          if (direction === directionCopy.backward) {
            currentDistance -= avgSpeed * deltaTime; // Subtract for backward moves
          } else {
            currentDistance += avgSpeed * deltaTime; // Add for forward moves
          }
        }

        if (direction === directionCopy.backward) {
          currentSpeed *= -1; // Make speed negative for backward moves
        }

        data.push({
          time: time + accumulatedTime,
          speed: currentSpeed,
          distance: currentDistance + lastDistance
        });
      }
    });

    accumulatedTime = Number(accumulatedTime) + Number(travelTime);
    accumulatedTime += Number(dwellTime || 0);

    // push dwellTime
    data.push({
      time: accumulatedTime,
      speed: currentSpeed,
      distance: currentDistance + lastDistance
    });

    lastDistance += currentDistance;

    const generatedMove = { name: stepName, id, data };
    generatedMoves.push(generatedMove);
  });

  return generatedMoves;
};

export const newMaxMovements = (moves, startPosition, strokeLength, directionCopy) => {
  const maxMovements = moves.map((move, index) => {
    const totalMovement =
      Number(startPosition) +
      moves.slice(0, index).reduce((acc, currentMove) => {
        if (currentMove.direction === directionCopy.forward) {
          return Number(acc) + Number(currentMove.movement);
        }
        return Number(acc) - Number(currentMove.movement);
      }, 0);

    const maxMovement =
      move.direction === directionCopy.forward ? Number(strokeLength) - Number(totalMovement) : Number(totalMovement);

    return maxMovement;
  });
  return maxMovements;
};

/**
 * Function preCalcTotalCycleTime provides the sum of traveltime and dwelltime for a set of moves.
 *
 * @param {*} moves
 * @returns int
 */
export const preCalcTotalCycleTime = (moves) => {
  var preCalcTotalCycleTime = 0;

  for (const m of moves) {
    preCalcTotalCycleTime += m.travelTime + m.dwellTime;
  }

  return preCalcTotalCycleTime;
};

/**
 * Function totalCycleHourMax provides maximum cycles amount per hour found for a set of moves.
 *
 * @param {*} moves
 * @returns int
 */
export const maxNumberCyclesPerHour = (moves) => {
  const MINUTES_PER_HOUR = 60;
  const SECONDS_PER_MINUTE = 60;
  const MILLISECONDS_PER_SECONDS = 1000;

  return Math.round((MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECONDS) / preCalcTotalCycleTime(moves));
};
